import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { NOTIFICATIONS_URL } from "./useNotifications";
import {
  AcceptSharingRequestBody,
  InsertSharingRequestBody,
  OwnSharingRequest,
  SharingRequest,
  SharingRequestResource,
} from "./types/sharingRequests";

/** The sharing requests of projects or calculations: the same calls for both, under their own paths */
export const useSharingRequestsApi = (resource: SharingRequestResource) => {
  const api = useApi();

  const getResourcesUrl = useCallback((isAdmin?: boolean) => `${isAdmin ? "/admin" : ""}/${resource}`, [resource]);

  const getBaseUrl = useCallback(
    (resourceId: string, isAdmin?: boolean) => `${getResourcesUrl(isAdmin)}/${resourceId}/sharing_requests`,
    [getResourcesUrl],
  );

  const getRequestUrl = useCallback(
    (requestId: string, isAdmin?: boolean) => `${getResourcesUrl(isAdmin)}/sharing_requests/${requestId}`,
    [getResourcesUrl],
  );

  // An answered request leaves the pending ones and the notifications; accepted, it also adds a sharing and changes the
  // list "shared" flag
  const mutateSharingRequests = useCallback(
    (resourceId: string, requestId: string, isAdmin?: boolean) => {
      const sharingsPath = resource === "projects" ? "sharings" : "sharing";
      mutateCache([
        getResourcesUrl(isAdmin),
        getBaseUrl(resourceId, isAdmin),
        getRequestUrl(requestId),
        `${getResourcesUrl(isAdmin)}/${resourceId}/${sharingsPath}`,
        NOTIFICATIONS_URL,
      ]);
    },
    [resource, getResourcesUrl, getBaseUrl, getRequestUrl],
  );

  // the pending requests of a project or calculation, to its owner (or an admin)
  const useSharingRequests = useCallback(
    (resourceId: string, query: PaginationQuery, signal: AbortSignal, isAdmin?: boolean) => {
      const url = buildUrl(getBaseUrl(resourceId, isAdmin), query);
      return useSWR<Paginated<SharingRequest>, CustomError, string>(url, (url) =>
        api.get<Paginated<SharingRequest>>({ url, signal }),
      );
    },
    [api, getBaseUrl],
  );

  // a request, whatever its state, to the owner of its project or calculation
  const useSharingRequest = useCallback(
    (requestId: string, signal: AbortSignal) =>
      useSWR<SharingRequest, CustomError, string>(getRequestUrl(requestId), (url) =>
        api.get<SharingRequest>({ url, signal }),
      ),
    [api, getRequestUrl],
  );

  // the current user's request for a project or calculation not shared with them
  const useOwnSharingRequest = useCallback(
    (resourceId: string, signal: AbortSignal) =>
      useSWR<OwnSharingRequest, CustomError, string>(`${getBaseUrl(resourceId)}/mine`, (url) =>
        api.get<OwnSharingRequest>({ url, signal }),
      ),
    [api, getBaseUrl],
  );

  const insertSharingRequest = useCallback(
    async (resourceId: string, body: InsertSharingRequestBody): Promise<void> => {
      await api.post<{ id: string }, InsertSharingRequestBody>(getBaseUrl(resourceId), body);
      mutateCache([`${getBaseUrl(resourceId)}/mine`]);
    },
    [api, getBaseUrl],
  );

  const acceptSharingRequest = useCallback(
    async (resourceId: string, requestId: string, edit: boolean, isAdmin?: boolean): Promise<void> => {
      await api.put<AcceptSharingRequestBody>(`${getRequestUrl(requestId, isAdmin)}/accept`, { edit });
      mutateSharingRequests(resourceId, requestId, isAdmin);
    },
    [api, getRequestUrl, mutateSharingRequests],
  );

  const rejectSharingRequest = useCallback(
    async (resourceId: string, requestId: string, isAdmin?: boolean): Promise<void> => {
      await api.put(`${getRequestUrl(requestId, isAdmin)}/reject`, {});
      mutateSharingRequests(resourceId, requestId, isAdmin);
    },
    [api, getRequestUrl, mutateSharingRequests],
  );

  return {
    useSharingRequests,
    useSharingRequest,
    useOwnSharingRequest,
    insertSharingRequest,
    acceptSharingRequest,
    rejectSharingRequest,
  };
};
